import { createSignal } from 'solid-js'
import { NotAuthenticatedError, authApi } from '../api/auth.api'
import { bindReferral } from '../api/endpoints/referrals.api'
import type { AuthUser, Candidate, HiringManager, UserType } from '../api/types/generated'
import { resetOnboardingSession } from '../features/candidate-activation/onboarding.store'
import { identifyUser, resetUser } from '../posthog'
import { clearStoredRefCode, getStoredRefCode } from '../shared/utils/referral'
import { activationStore } from './activation.store'
import { candidateNotificationStore } from './candidate-notification.store'
import { creditsStore } from './credits.store'
import { gamificationStore } from './gamification.store'
import { resetPreferences } from './preferences.store'
import { workspaceStore } from './workspace.store'

interface AuthState {
  user: AuthUser | null
  userType: UserType | null
  isAuthenticated: boolean
  isLoading: boolean
}

function isCandidate(user: AuthUser): user is Candidate {
  return 'work_status' in user
}

function isHiringManager(user: AuthUser): user is HiringManager {
  return !('work_status' in user)
}

const [authState, setAuthState] = createSignal<AuthState>({
  user: null,
  userType: null,
  isAuthenticated: false,
  isLoading: true,
})

function bindPendingReferral() {
  const code = getStoredRefCode()
  if (!code) {
    return
  }
  void bindReferral(code).finally(() => clearStoredRefCode())
}

let sessionBootstrapped = false

function bootstrapSession(user: AuthUser, userType: UserType) {
  sessionBootstrapped = true
  identifyUser(user.id, userType as 'candidate' | 'hiring_manager', {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
  })
  if (userType === 'candidate') {
    void creditsStore.loadAll()
    bindPendingReferral()
  }
}

export const authStore = {
  getState: authState,

  get state() {
    return authState()
  },

  get isEmailVerified(): boolean {
    const state = authState()
    if (!state.user) {
      return false
    }
    if (isCandidate(state.user)) {
      return state.user.email_verified
    }
    if (isHiringManager(state.user)) {
      return state.user.email_verified
    }
    return false
  },

  get hmApprovalStatus(): 'pending' | 'approved' | 'rejected' | null {
    const state = authState()
    if (!state.user || isCandidate(state.user)) {
      return null
    }
    return (state.user as HiringManager).approval_status ?? null
  },

  get isHMApproved(): boolean {
    const state = authState()
    if (!state.user || isCandidate(state.user)) {
      return true
    }
    return (state.user as HiringManager).approval_status === 'approved'
  },

  get deletionScheduledFor(): string | null {
    const state = authState()
    if (!state.user || !isCandidate(state.user)) {
      return null
    }
    return state.user.deletion_scheduled_for ?? null
  },

  get candidateNeedsName(): boolean {
    const state = authState()
    if (!state.user || state.userType !== 'candidate') {
      return false
    }
    return state.user.first_name.trim() === ''
  },

  setUser(user: AuthUser, userType: UserType) {
    setAuthState({
      user,
      userType,
      isAuthenticated: true,
      isLoading: false,
    })
    bootstrapSession(user, userType)
    if (userType === 'candidate') {
      void gamificationStore.refresh()
    }
  },

  completeBootstrap() {
    const { user, userType, isAuthenticated } = authState()
    if (sessionBootstrapped || !isAuthenticated || !user || !userType) {
      return
    }
    bootstrapSession(user, userType)
  },

  clearUser() {
    sessionBootstrapped = false
    resetOnboardingSession()
    setAuthState({
      user: null,
      userType: null,
      isAuthenticated: false,
      isLoading: false,
    })
  },

  setLoading(isLoading: boolean) {
    setAuthState((prev) => ({ ...prev, isLoading }))
  },

  async checkAuth() {
    setAuthState((prev) => ({ ...prev, isLoading: true }))
    try {
      const response = await authApi.getCurrentUser()
      setAuthState({
        user: response.user,
        userType: response.user_type,
        isAuthenticated: true,
        isLoading: false,
      })
      bootstrapSession(response.user, response.user_type)
      return true
    } catch {
      sessionBootstrapped = false
      setAuthState({
        user: null,
        userType: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return false
    }
  },

  async revalidate() {
    try {
      const response = await authApi.getCurrentUser()
      const prev = authState()
      if (
        prev.isAuthenticated &&
        prev.userType === response.user_type &&
        JSON.stringify(prev.user) === JSON.stringify(response.user)
      ) {
        return
      }
      setAuthState({
        user: response.user,
        userType: response.user_type,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (err) {
      if (err instanceof NotAuthenticatedError && err.status === 401) {
        sessionBootstrapped = false
        setAuthState({ user: null, userType: null, isAuthenticated: false, isLoading: false })
      }
    }
  },

  async logout() {
    try {
      await authApi.logout()
      this.clearUser()
    } catch (error) {
      console.error('Logout failed:', error)
      this.clearUser()
    }
    workspaceStore.clear()
    creditsStore.clear()
    activationStore.reset()
    candidateNotificationStore.clear()
    gamificationStore.reset()
    resetPreferences()
    clearStoredRefCode()
    resetUser()
  },
}
